import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip
} from '@mui/material';
import { 
  Face as FaceIcon, 
  Search as SearchIcon,
  PhotoLibrary as PhotoLibraryIcon 
} from '@mui/icons-material';
import faceRecognitionService from '../services/faceRecognitionService';
import AuthenticatedAvatar from './AuthenticatedAvatar';
import FaceRecognitionProgress from './FaceRecognitionProgress';
import ProgressMonitor from '../utils/ProgressMonitor';

/**
 * Dialog to display face recognition matches for a reference person
 */
const FaceRecognitionResultsDialog = ({ open, onClose, sessionId, referencePersonId, referencePersonName, referenceImage }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [matches, setMatches] = useState([]);
  const [showProgress, setShowProgress] = useState(false);
  const [progressData, setProgressData] = useState({
    percentage: 0,
    current: 0,
    total: 0,
    status: 'Initializing...',
    isCompleted: false,
    hasError: false,
    results: null
  });
  const progressMonitorRef = useRef(null);

  useEffect(() => {
    if (open && sessionId && referencePersonId) {
      loadMatches();
    }
  }, [open, sessionId, referencePersonId]);

  // Cleanup progress monitor when dialog closes
  useEffect(() => {
    if (!open && progressMonitorRef.current) {
      progressMonitorRef.current.stop();
      progressMonitorRef.current = null;
      setShowProgress(false);
      setLoading(false);
    }
  }, [open]);

  const loadMatches = async () => {
    setLoading(true);
    setError(null);
    setShowProgress(true);
    setProgressData({
      percentage: 0,
      current: 0,
      total: 0,
      status: 'Initializing face recognition...',
      isCompleted: false,
      hasError: false,
      results: null
    });
    
    try {
      // Stop any existing progress monitor
      if (progressMonitorRef.current) {
        progressMonitorRef.current.stop();
      }
      
      // Start progress monitoring
      progressMonitorRef.current = new ProgressMonitor(`find-matches-${sessionId}-${referencePersonId}`);
      
      progressMonitorRef.current.onProgress((data) => {
        setProgressData(data);
      });
      
      progressMonitorRef.current.onComplete((data) => {
        setProgressData(data);
        setMatches(data.results?.matches || []);
        setLoading(false);
        setShowProgress(false);
      });
      
      progressMonitorRef.current.onError((data) => {
        setProgressData(data);
        setError(data.error || 'Failed to find person matches');
        setLoading(false);
        setShowProgress(false);
      });
      
      progressMonitorRef.current.start();
      
      // Make API call
      const result = await faceRecognitionService.findPersonMatches(sessionId, referencePersonId);
      
      // If no progress was reported (fallback)
      if (!progressData.isCompleted && !progressData.hasError) {
        setMatches(result.matches || []);
        setLoading(false);
        setShowProgress(false);
      }
    } catch (err) {
      setLoading(false);
      setShowProgress(false);
      setError(err.response?.data?.error || 'Failed to find person matches');
    } finally {
      if (progressMonitorRef.current) {
        progressMonitorRef.current.stop();
        progressMonitorRef.current = null;
      }
    }
  };

  // Calculate match percentage for display
  const getConfidenceDisplay = (confidence) => {
    // Convert from 0-100 to a more user-friendly scale
    const matchPercentage = Math.round(confidence);
    
    // Return chip with color based on confidence
    let color = 'error';
    if (matchPercentage >= 90) color = 'success';
    else if (matchPercentage >= 70) color = 'primary';
    else if (matchPercentage >= 50) color = 'warning';
    
    return (
      <Chip 
        size="small" 
        color={color}
        label={`${matchPercentage}% match`}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <FaceIcon sx={{ mr: 1 }} />
          Face Recognition Results
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {referenceImage && referencePersonName && (
          <Box display="flex" alignItems="center" mb={3}>
            <Box sx={{ mr: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Reference Person
              </Typography>
              <AuthenticatedAvatar 
                imageId={referenceImage.uniqueId}
                alt={referencePersonName}
                variant="rounded"
                sx={{ width: 80, height: 80 }}
              />
            </Box>
            <Box>
              <Typography variant="h6">
                {referencePersonName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Looking for this person across all photos in the session
              </Typography>
            </Box>
          </Box>
        )}
        
        {showProgress ? (
          <FaceRecognitionProgress 
            progress={progressData}
            title="Finding Face Matches"
            description="Analyzing photos to find matching faces..."
          />
        ) : loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" ml={2}>
              Searching for matches...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        ) : matches.length > 0 ? (
          <>
            <Typography variant="subtitle1" gutterBottom>
              <SearchIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
              Found {matches.length} {matches.length === 1 ? 'match' : 'matches'}
            </Typography>
            
            <List>
              {matches.map((match, index) => (
                <ListItem 
                  key={index}
                  divider={index < matches.length - 1}
                  alignItems="flex-start"
                >
                  <ListItemAvatar>
                    <AuthenticatedAvatar 
                      imageId={match.imageUniqueId || match.imageBlobReference}
                      alt={`Match ${index + 1}`}
                      variant="rounded"
                      sx={{ width: 100, height: 100 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2">
                          {match.imageName || `Image ${index + 1}`}
                        </Typography>
                        {getConfidenceDisplay(match.confidence)}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" component="span">
                        {match.boundingBox && (
                          <span>
                            Position: ({Math.round(match.boundingBox.left)}, {Math.round(match.boundingBox.top)})
                          </span>
                        )}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <Box textAlign="center" py={4}>
            <PhotoLibraryIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No matches found for this person in the session.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
        {!loading && matches.length > 0 && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={loadMatches}
          >
            Refresh Results
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FaceRecognitionResultsDialog;
